import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, firestore } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any
// child component that calls useAuth().
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to fetch user role from Firestore
  const fetchUserRole = async (uid) => {
    try {
      const userDoc = await getDoc(doc(firestore, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role || 'regular_user'); // Default to regular_user if role is not set
        return userData.role;
      } else {
        console.log('No user document found for this user');
        setUserRole('regular_user'); // Default role
        return 'regular_user';
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('regular_user'); // Default role
      return 'regular_user';
    }
  };

  // Function to handle user logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUserRole(null);
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      return false;
    }
  };

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch user role when user is authenticated
        await fetchUserRole(user.uid);
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // The value passed to the context provider
  const value = {
    currentUser,
    userRole,
    isAuthenticated: !!currentUser,
    isBusinessUser: userRole === 'business_user',
    isRegularUser: userRole === 'regular_user',
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
